import Grid from "@mui/material/Grid2";
import { useContext, useEffect, useState } from "react";
import { APIContext } from "../../Contexts/APIContext";
import Paper from '@mui/material/Paper';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import * as React from 'react';
import ResponsiveTable from '../../Utilities/ResponsiveTable';
import OrganisationRow from './OrganisationRow';
import OrganisationTableRowViewModel from './ViewModels/OrganisationTableRowViewModel';
import OrgFilterBar from './OrgFilterBar';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Line from "../../Utilities/Line";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { useNavigate } from "react-router-dom";
import SortableTableHead, { HeadCell } from "../../Utilities/SortableTableHead";
import TableCell from "@mui/material/TableCell";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import OfflineAuthDialog from "../../Licensing/Organisation/OfflineAuthDialog";
import RoleCheck from "../../Authorisation/RoleCheck";
import { OrganisationsContext } from '../../Contexts/OrganisationsContext';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

const headCells: HeadCell<OrganisationTableRowViewModel>[] = [
    {
        id: 'organisationName',
        property: 'OrganisationName',
        disablePadding: false,
        label: 'Organisation Name',
        show: true
    },
    {
        id: 'licenseType',
        property: 'LicenseType',
        disablePadding: false,
        label: 'License Type',
        show: true
    },
    {
        id: 'expiryDate',
        property: 'ExpiryDate',
        disablePadding: false,
        label: 'Expiry Date',
        show: true
    },
    {
        id: 'renewalDate',
        property: 'RenewalDate',
        disablePadding: false,
        label: 'Renewal Date',
        show: true
    },
    {
        id: 'invoicePayPeriod',
        property: 'InvoicePayPeriod',
        disablePadding: false,
        label: 'Invoice Pay Period',
        show: true
    },
    {
        id: 'invoicePayMethod',
        property: 'InvoicePayMethod',
        disablePadding: false,
        label: 'Invoice Pay Method',
        show: true
    },
    {
        id: 'invoiceNotice',
        property: 'InvoiceNotice',
        disablePadding: false,
        label: 'Invoice Notice',
        show: true
    },
    {
        id: 'estimateNotice',
        property: 'EstimateNotice',
        disablePadding: false,
        label: 'Estimate Notice',
        show: true
    },
];

export type Order = 'asc' | 'desc';


export default function ManageOrganisations() {
    const { post } = useContext(APIContext);
    const { filterState, updateFilterState, searchResults, updateResultsState } = useContext(OrganisationsContext);
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [authOpen, setAuthOpen] = useState(false);
    const [quickSearchInput, setQuickSearchInput] = useState(filterState.quickSearch ?? "");

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        getData();
    }, [filterState]);

    useEffect(() => {
        setQuickSearchInput(filterState.quickSearch ?? "");
    }, [filterState.quickSearch]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const trimmed = quickSearchInput.trim();
            const nextValue = trimmed.length > 0 ? trimmed : null;

            if ((filterState.quickSearch ?? null) !== nextValue) {
                updateFilterState({ quickSearch: nextValue, pageNumber: 0 });
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [quickSearchInput, filterState.quickSearch, updateFilterState]);

    const getData = async () => {
        post("Organisation", `GetOrganisationTable?order=${filterState.order}&orderBy=${filterState.orderByProp}`, filterState, updateResultsState);
    };

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof OrganisationTableRowViewModel) => {
        const isAsc = filterState.order === 'asc';
        const orderByProp = headCells.find(f => f.id === filterState.orderBy)!.property;
        updateFilterState({ order: isAsc ? 'desc' : 'asc', orderBy: property, orderByProp: orderByProp });
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        updateFilterState({ pageNumber: newPage });
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateFilterState({ rowsPerPage: parseInt(event.target.value, 10), pageNumber: 0 });
    };

    const openAuthDialog = () => {
        setAuthOpen(true);
        setAnchorEl(null);
    }

    const viewOrganisation = (id: string) => {
        navigate(`/admin/manageorganisation?organisationid=${id}`);
    }

    return (
        <RoleCheck requriedRole={["Admin"]}>
            <Paper sx={{ height: '100%', pt: '12px', flexDirection: "column", display: "flex" }}>
                <Grid container>
                    <Grid>
                        <Typography variant="h4">Manage Organisations</Typography>
                    </Grid>
                    <Grid>
                        <Button endIcon={<KeyboardArrowDownIcon />} onClick={handleClick}>Management Actions</Button>
                        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                            <MenuItem onClick={() => navigate(`/admin/createorganisation`)}>Add New Organisation</MenuItem>
                            <MenuItem onClick={openAuthDialog}>Offline System Authentication</MenuItem>
                        </Menu>
                    </Grid>
                    <Line />
                    <Grid size="grow">
                        <OrgFilterBar />
                    </Grid>
                    <Grid container spacing={1} alignItems="center">
                        <Grid>
                            <TextField
                                size="small"
                                sx={{ minWidth: 320 }}
                                label="Search"
                                placeholder="Search organisations, member no, office, town..."
                                value={quickSearchInput}
                                onChange={(e) => setQuickSearchInput(e.target.value)}
                            />
                        </Grid>
                        <Grid>
                            <FormControlLabel
                                control={<Switch
                                    checked={filterState.archivedOnly}
                                    onChange={(event) => updateFilterState({ archivedOnly: event.target.checked, pageNumber: 0 })}
                                />}
                                label="Show archived organisations"
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Box flexGrow={1} overflow="hidden" flexDirection="column" display="flex" paddingTop="8px">
                    <TableContainer>
                        <ResponsiveTable stickyHeader={true}>
                            <SortableTableHead
                                order={filterState.order || 'asc'}
                                orderBy={filterState.orderBy
                                }
                                onRequestSort={handleRequestSort}
                                headCells={headCells}
                                lastCells={<TableCell>Primary Office Location</TableCell>}
                            />
                            <TableBody sx={{ width: "100%" }}>
                                {(searchResults !== null && searchResults.rows.length > 0) &&
                                    searchResults.rows.map((row: OrganisationTableRowViewModel) => {
                                        return (<OrganisationRow key={row.id} row={row} refresh={getData} viewOrg={viewOrganisation} />);
                                    })
                                }
                            </TableBody>
                        </ResponsiveTable>
                    </TableContainer>
                    {searchResults !== null &&
                        <Box>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                component="div"
                                count={searchResults.count}
                                rowsPerPage={filterState.rowsPerPage}
                                page={filterState.pageNumber}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Box>
                    }
                </Box>
                <OfflineAuthDialog open={authOpen} close={() => setAuthOpen(false)} />
            </Paper >
        </RoleCheck>
    );
}
